import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, Tag, CreditCard, ShoppingCart } from 'lucide-react';
import { motion, useAnimationControls } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useOffersAvailability } from '../hooks/useOffersAvailability';
import { trackDiscovery } from '../utils/analytics';

/**
 * Haptic tick on tab tap. Defaults to LIGHT; pass 'MEDIUM' for a heavier
 * thunk on tab-change (re-tap of the already-active tab stays LIGHT).
 * Uses the runtime Capacitor bridge so no JS shim install (and therefore no
 * new AAB) is required. Silent no-op on web, iOS web preview, or whenever
 * the Haptics plugin isn't part of the native build. The bridge call returns
 * a Promise; we swallow rejections so a slow native side never blocks
 * navigation.
 */
function tapHaptic(style = 'LIGHT') {
  try {
    if (typeof window === 'undefined') return;
    const Capacitor = window.Capacitor;
    if (!Capacitor?.isNativePlatform?.()) return;
    const Haptics = Capacitor?.Plugins?.Haptics;
    if (!Haptics?.impact) return;
    Haptics.impact({ style }).catch(() => {});
  } catch {
    /* defensive — never block nav */
  }
}

/**
 * One tab in the bottom nav. Owns its own AnimationControls so the
 * activation pop (icon + label scale-up when becoming active) and re-tap
 * wiggle (small bounce when tapping the already-active tab) run independently
 * per tab. Press feedback is a Framer Motion spring `whileTap` so release
 * overshoots back into place — that's the Zomato-style "button press" feel.
 */
function NavTab({ to, Icon, label, isActive, badge, showOffersDot, testId }) {
  const controls = useAnimationControls();
  const wasActiveRef = useRef(isActive);

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      controls.start({
        scale: [1, 1.18, 1],
        transition: { duration: 0.35, ease: [0.34, 1.56, 0.64, 1] },
      });
    }
    wasActiveRef.current = isActive;
  }, [isActive, controls]);

  const handleClick = () => {
    if (isActive) {
      tapHaptic('LIGHT');
      controls.start({
        scale: [1, 0.92, 1.04, 1],
        transition: { duration: 0.3, ease: 'easeOut' },
      });
    } else {
      tapHaptic('MEDIUM');
      // Origination telemetry — a Menu/Offers tab activation starts a discovery journey
      if (to === '/menu') trackDiscovery('nav_menu_tab');
      else if (to === '/offer-of-the-day') trackDiscovery('nav_offers_tab');
    }
  };

  return (
    <Link
      to={to}
      state={{ fromTab: true }}
      onClick={handleClick}
      className={`relative flex flex-col items-center gap-0.5 px-3 native:px-2 py-1 native:flex-1 native:min-w-0 transition-colors ${
        isActive ? 'text-accent' : 'text-white/50'
      }`}
      data-testid={testId}
    >
      {isActive && (
        <motion.div
          layoutId="activeTabPill"
          className="absolute inset-x-1 inset-y-0 bg-accent/15 rounded-2xl pointer-events-none"
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
      <motion.div
        className="relative z-10 flex flex-col items-center gap-0.5"
        animate={controls}
        whileTap={{ scale: 0.85 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        <div className="relative">
          <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 1.8} />
          {badge > 0 && (
            <span className="absolute -top-1.5 -right-2.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {badge > 9 ? '9+' : badge}
            </span>
          )}
          {showOffersDot && (
            <span
              className="absolute -top-1 -right-1.5 w-2.5 h-2.5 bg-accent rounded-full animate-pulse"
              data-testid="nav-offers-dot"
            />
          )}
        </div>
        <span className={`text-[10px] whitespace-nowrap ${isActive ? 'font-bold' : 'font-medium'}`}>{label}</span>
      </motion.div>
    </Link>
  );
}

/**
 * Fixed bottom navigation bar with cart badge, pulsing Offers dot when there's
 * something to claim, and conditional Plans tab. Tab taps fire a Zomato-style
 * press: spring `whileTap` scale, sliding active pill via `layoutId`, icon-pop
 * on activation, re-tap wiggle on the already-active tab, plus a haptic tick
 * (MEDIUM on tab-change, LIGHT on re-tap).
 */
export default function BottomNav() {
  const location = useLocation();
  const { cart } = useCart();
  const { subscriptions_visible } = useFeatureFlags();
  const { hasOffers } = useOffersAvailability();
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const allNavItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/menu', icon: LayoutGrid, label: 'Menu' },
    { path: '/offer-of-the-day', icon: Tag, label: 'Offers' },
    { path: '/subscriptions', icon: CreditCard, label: 'Plans', hidden: !subscriptions_visible },
    { path: '/cart', icon: ShoppingCart, label: 'Cart', badge: cartCount },
  ];

  const navItems = allNavItems.filter(item => !item.hidden);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-primary border-t border-white/10" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="bottom-nav">
      <div className="flex items-center justify-around py-2 native:py-1.5 max-w-lg mx-auto">
        {navItems.map(({ path, icon: Icon, label, badge }) => {
          const isActive = location.pathname === path;
          const showOffersDot = path === '/offer-of-the-day' && !isActive && hasOffers;
          return (
            <NavTab
              key={path}
              to={path}
              Icon={Icon}
              label={label}
              isActive={isActive}
              badge={badge}
              showOffersDot={showOffersDot}
              testId={`nav-${label.toLowerCase()}`}
            />
          );
        })}
      </div>
    </nav>
  );
}
